import { useLayoutEffect, useState } from 'react'
import { useActiveOrganization } from './use-organization'
import { Feature } from 'api/model'
import { usersCheckPermission } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { featurePermissionsAtomFamily, isLoadingPermissionsAtomFamily, PermissionFeature } from 'state/permission'
import { useRecoilState } from 'recoil'

function useFetchCheckPermissions(feature: Feature) {
  const activeOrganization = useActiveOrganization()
  const [featurePermissions, setFeaturePermissions] = useRecoilState<PermissionFeature | null>(
    featurePermissionsAtomFamily(feature)
  )
  const [isLoading, setIsLoading] = useRecoilState(isLoadingPermissionsAtomFamily(feature))
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !feature || isLoading) {
        return
      }

      setIsLoading(true)

      try {
        const response = await usersCheckPermission({ feature })

        // update the atom based on the feature that we're passing in
        const featurePermissions = {
          isPermitted: response.data.is_permitted,
          acceptedPermissions: response.data.accepted_permissions,
          missingPermissions: response.data.missing_permissions,
        }

        setFeaturePermissions(featurePermissions)
        setError(null)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    // we only want to fetch data if we don't have any feature permissions yet
    if (!featurePermissions && !error) {
      fetchData()
    }
  }, [activeOrganization, feature, setFeaturePermissions, featurePermissions, error, isLoading, setIsLoading])

  return {
    isLoading,
    featurePermissions,
    error,
  }
}

export { useFetchCheckPermissions }
